import { useState } from 'react';
import { Copy, Download } from 'lucide-react';
import { AGENT_PLATFORMS, agentInstallCommands, encodeSetupCode, isGpuPool } from '@vidmakr/shared';
import { Button, cx } from './ui';
import { toast } from '@/lib/toast';

export interface WorkerToken {
  token: string;
  apiUrl: string;
  image: string;
  name: string;
  pools: string[];
}

type Os = 'windows' | 'linux' | 'macos';

function guessOs(): Os {
  const p = (navigator.platform || navigator.userAgent).toLowerCase();
  return p.includes('win') ? 'windows' : p.includes('mac') ? 'macos' : 'linux';
}

const OS_TABS: { id: Os; label: string; where: string }[] = [
  { id: 'windows', label: 'Windows', where: 'In PowerShell opened as Administrator (Start → type PowerShell → Run as administrator):' },
  { id: 'linux', label: 'Linux', where: 'In a terminal:' },
  { id: 'macos', label: 'macOS', where: 'In Terminal, after brew install ffmpeg (Apple silicon; Macs have no NVIDIA GPU, so it renders on the CPU):' },
];

const copy = (text: string, what: string) => void navigator.clipboard.writeText(text).then(() => toast.success(`${what} copied`));

export function CommandBox({ title, text, what }: { title: string; text: string; what: string }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted">{title}</span>
        <Button size="sm" variant="primary" onClick={() => copy(text, what)}>
          <Copy size={14} /> Copy
        </Button>
      </div>
      <pre className="selectable max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-bg p-3 font-mono text-xs">{text}</pre>
    </div>
  );
}

/**
 * How to start a worker on a machine: the installer (one command that sets
 * up the vidmakr-worker agent as a background service, no Docker), or a
 * docker run command.
 */
export function WorkerSetup({ t, packageHint = false }: { t: WorkerToken; packageHint?: boolean }) {
  const gpu = t.pools.some(isGpuPool);
  const [mode, setMode] = useState<'installer' | 'docker'>('installer');
  const [os, setOs] = useState<Os>(guessOs);
  const code = encodeSetupCode({ apiUrl: t.apiUrl, token: t.token, name: t.name });
  const commands = agentInstallCommands(t.apiUrl, code);
  const container = `vidmakr-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'worker'}`;
  // One line, so it pastes the same into PowerShell, cmd, bash and zsh.
  const docker = [
    'docker run -d',
    `--name ${container}`,
    '--restart unless-stopped',
    gpu ? '--gpus all' : '',
    `-e API_URL=${t.apiUrl}`,
    `-e WORKER_TOKEN=${t.token}`,
    `-e WORKER_NAME="${t.name}"`,
    t.image,
  ]
    .filter(Boolean)
    .join(' ');
  const tab = (active: boolean) => cx('flex-1 rounded-md px-2 py-1.5 text-xs', active ? 'bg-panel-3 text-fg' : 'text-muted hover:text-fg');
  const step = 'flex gap-3 text-sm';
  const num = 'flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/20 text-[11px] font-semibold text-accent-2';
  return (
    <div className="space-y-4" data-worker-setup>
      <p className="text-sm text-muted">
        The token is shown once. The worker only makes outbound connections, so it works behind a home router with no port forwarding.
      </p>
      <div className="flex gap-1 rounded-lg bg-panel-2 p-1" data-setup-mode>
        <button className={tab(mode === 'installer')} onClick={() => setMode('installer')}>
          Installer (recommended)
        </button>
        <button className={tab(mode === 'docker')} onClick={() => setMode('docker')}>
          Docker
        </button>
      </div>

      {mode === 'installer' ? (
        <>
          {gpu ? (
            <div className={step}>
              <span className={num}>1</span>
              <span>Install a current NVIDIA driver. Nothing else is needed: the installer brings its own ffmpeg with NVENC.</span>
            </div>
          ) : null}
          <div className="flex gap-1 rounded-lg border border-line p-1" data-setup-os>
            {OS_TABS.map((o) => (
              <button key={o.id} className={tab(os === o.id)} onClick={() => setOs(o.id)}>
                {o.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted">{OS_TABS.find((o) => o.id === os)!.where}</p>
          <CommandBox title="Install" text={commands[os]} what="Command" />
          <div className="space-y-1 text-xs text-muted">
            <div>
              It runs in the background and starts when the machine boots ({os === 'windows' ? 'a scheduled task' : os === 'macos' ? 'a launchd service' : 'a systemd service'}
              ), updates itself, and shows up here as online within a few seconds. Check on it with{' '}
              <code className="font-mono">vidmakr-worker status</code>; remove it with <code className="font-mono">vidmakr-worker uninstall</code>.
            </div>
            <div>
              Or download it and paste the setup code when it asks:{' '}
              {AGENT_PLATFORMS.map((p, i) => (
                <span key={p.id}>
                  {i ? ' · ' : ''}
                  <a className="text-accent-2 hover:underline" href={`${t.apiUrl}/downloads/worker/${p.file}`}>
                    <Download size={11} className="mr-0.5 inline" />
                    {p.label}
                  </a>
                </span>
              ))}
            </div>
          </div>
          <Button size="sm" onClick={() => copy(code, 'Setup code')} data-copy-setup-code>
            <Copy size={14} /> Copy setup code
          </Button>
        </>
      ) : (
        <>
          {gpu ? (
            <div className="space-y-2.5 rounded-xl border border-line bg-panel-2 p-3.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted">On the GPU machine (once)</div>
              <div className={step}>
                <span className={num}>1</span>
                <span>Install a current NVIDIA driver.</span>
              </div>
              <div className={step}>
                <span className={num}>2</span>
                <span>
                  <b>Windows:</b> install Docker Desktop and keep “Use the WSL 2 based engine” on (the default). <b>Linux:</b> install Docker and the
                  NVIDIA Container Toolkit.
                </span>
              </div>
              <div className={step}>
                <span className={num}>3</span>
                <span>
                  Check GPU access: <code className="rounded bg-bg px-1 font-mono text-xs">docker run --rm --gpus all ubuntu nvidia-smi</code> should
                  list your GPU.
                </span>
              </div>
            </div>
          ) : null}
          <CommandBox title="Start the worker" text={docker} what="Command" />
          <div className="space-y-1 text-xs text-muted">
            <div>
              It starts with the machine and restarts on failure. It appears here as online within a few seconds;{' '}
              <code className="font-mono">docker logs {container}</code> should say <code className="font-mono">NVENC: yes</code>
              {gpu ? '' : ' on a GPU machine'}.
            </div>
            <div>
              Update: <code className="font-mono">docker pull {t.image}</code>, then remove and re-run the container. Stop:{' '}
              <code className="font-mono">docker rm -f {container}</code>.
            </div>
            {packageHint ? (
              <div>
                If the pull is denied, make the <code className="font-mono">vidmakr-worker</code> package public on GitHub, or run{' '}
                <code className="font-mono">docker login ghcr.io</code> first.
              </div>
            ) : null}
          </div>
          <Button size="sm" onClick={() => copy(t.token, 'Token')}>
            <Copy size={14} /> Copy token only
          </Button>
        </>
      )}
    </div>
  );
}

const EXE = '"$env:ProgramFiles\\vidmakr-worker\\vidmakr-worker.exe"';
const WINDOWS_COMMANDS = [
  { title: 'Update to the latest build and restart', what: 'Update command', text: `& ${EXE} update` },
  { title: 'Reinstall the latest build even if it looks current', what: 'Update command', text: `& ${EXE} update --force` },
  { title: 'Restart only (e.g. after changing shares)', what: 'Restart command', text: 'Stop-ScheduledTask -TaskName vidmakr-worker; Start-ScheduledTask -TaskName vidmakr-worker' },
  { title: 'Check on it', what: 'Command', text: `& ${EXE} status\n& ${EXE} logs -f` },
];

/** Run-it-yourself update and restart commands for a Windows worker (PowerShell as Administrator). */
export function WorkerManualUpdate() {
  return (
    <details className="mt-5 rounded-xl border border-line bg-panel" data-worker-manual-update>
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">Update or restart a worker manually (Windows PowerShell)</summary>
      <div className="space-y-4 border-t border-line px-4 py-4">
        <p className="text-xs text-muted">
          Workers update themselves, and <b>Update and restart</b> in a worker’s menu asks one to. If it is offline, or stuck on a job (an update from the menu waits for the running job; a worker on an old build cannot be forced from here), run these on the worker machine in PowerShell
          opened as Administrator (Start → type PowerShell → Run as administrator).
        </p>
        {WINDOWS_COMMANDS.map((c) => (
          <CommandBox key={c.title} title={c.title} text={c.text} what={c.what} />
        ))}
        <p className="text-xs text-faint">
          The worker runs as the scheduled task <code className="font-mono">vidmakr-worker</code>; <code className="font-mono">update</code> restarts it by itself when a new build was installed.
        </p>
      </div>
    </details>
  );
}
